import { useEffect } from 'react';
import * as React from 'react';
import { TagsProvider } from '@sdk/tags/TagsContext';
import { AuthProvider } from '@sdk/authentication/AuthContext';
import { publish } from '@sdk/utils/websocketPubSub';
import { ClientFlags, SaveClientFlags } from '@sdk/authentication/types';
import { CurrencyProvider } from '@sdk/common/currency/CurrencyContext';
import { IntegrationType } from '@sdk/integrations/types';

import { NavigationProvider } from '@web/navigation/NavigationProvider';
import { NavigateAction } from '@web/navigation/navigationProvider.types';

import type { OpenPremiumGuardModal } from '../premiumGuard/PremiumGuard';
import { PremiumGuardProvider } from '../premiumGuard/PremiumGuardContext';

import { SettingsErrorsProvider } from './SettingsErrorContext';
import SettingsScreen, { Props as SettingsProps } from './SettingsScreen';
import { UserSettingsProvider } from './UserSettingsContext';
import { UserOrgSettingsProvider } from './UserOrgSettingsContext';
import { SettingsClientsFlagsProvider } from './SettingsClientFlagsContext';

type Props = {
  openPremiumGuardModal: OpenPremiumGuardModal;
  openPricingModal: () => void;
  openLaborCostVideoModal: () => void;
  setWebsocketCallback?: (data: { cb: (message: string, data: any) => void }) => void;
  clientFlags: ClientFlags;
  saveClientFlags: SaveClientFlags;
};

const SettingsWithContext: React.FC<React.PropsWithChildren<SettingsProps & Props>> = (props) => {
  useEffect(() => {
    props.setWebsocketCallback?.({ cb: publish });
  }, [props]);

  const navigate: NavigateAction = (path, params) => {
    props.$state.go(path, params);
  };

  return (
    <AuthProvider currentUser={props.user}>
      <NavigationProvider navigate={navigate}>
        <CurrencyProvider>
          <TagsProvider tagsList={props.personaCollection.tags}>
            <UserSettingsProvider settings={props.user.settings}>
              <UserOrgSettingsProvider settings={props.user.orgSettings}>
                <SettingsClientsFlagsProvider
                  clientFlags={props.clientFlags}
                  saveClientFlags={props.saveClientFlags}>
                  <PremiumGuardProvider
                    openPremiumGuardModal={props.openPremiumGuardModal}
                    openPricingModal={props.openPricingModal}
                    openLaborCostVideoModal={props.openLaborCostVideoModal}>
                    <SettingsErrorsProvider>
                      <SettingsScreen {...props} />
                    </SettingsErrorsProvider>
                  </PremiumGuardProvider>
                </SettingsClientsFlagsProvider>
              </UserOrgSettingsProvider>
            </UserSettingsProvider>
          </TagsProvider>
        </CurrencyProvider>
      </NavigationProvider>
    </AuthProvider>
  );
};

export default SettingsWithContext;
